import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiX, FiSearch, FiChevronRight } from 'react-icons/fi';
import { groupLocationsHierarchically, getRegionForCountry, parseLocation, REGIONS } from '../utils/locationGrouping';

/**
 * Hierarchical Location Dropdown with Region → Country → City structure
 */
function HierarchicalLocationDropdown({
  label,
  options = [],
  selectedValues = [],
  onSelectionChange,
  isDisabled = false,
  isOpen,
  onToggle
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('hierarchical'); // 'hierarchical' or 'flat'
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [expandedCountries, setExpandedCountries] = useState(new Set());

  // Parse selections into regions and countries
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  // Group locations
  const hierarchy = groupLocationsHierarchically(options);

  // Filter options based on search
  const filteredOptions = searchTerm
    ? options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Toggle region expansion
  const toggleRegion = (region) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };

  // Toggle country expansion
  const toggleCountry = (country) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(country)) {
      newExpanded.delete(country);
    } else {
      newExpanded.add(country);
    }
    setExpandedCountries(newExpanded);
  };

  // Select/deselect individual location
  const toggleLocation = (location) => {
    const newSelection = selectedValues.includes(location)
      ? selectedValues.filter(v => v !== location)
      : [...selectedValues, location];
    onSelectionChange(newSelection);
  };

  // Select all locations in a country
  const selectCountry = (country) => {
    const region = getRegionForCountry(country);
    const countryLocations = hierarchy[region]?.[country] || [];

    // Check if all locations in country are selected
    const allSelected = countryLocations.every(loc => selectedValues.includes(loc));

    if (allSelected) {
      // Deselect all
      const newSelection = selectedValues.filter(v => !countryLocations.includes(v));
      onSelectionChange(newSelection);
    } else {
      // Select all
      const newSelection = [...new Set([...selectedValues, ...countryLocations])];
      onSelectionChange(newSelection);
    }
  };

  // Select all locations in a region
  const selectRegion = (region) => {
    const regionLocations = [];
    Object.values(hierarchy[region] || {}).forEach(cities => {
      regionLocations.push(...cities);
    });

    // Check if all locations in region are selected
    const allSelected = regionLocations.every(loc => selectedValues.includes(loc));

    if (allSelected) {
      // Deselect all
      const newSelection = selectedValues.filter(v => !regionLocations.includes(v));
      onSelectionChange(newSelection);
    } else {
      // Select all
      const newSelection = [...new Set([...selectedValues, ...regionLocations])];
      onSelectionChange(newSelection);
    }
  };

  // Clear all selections
  const clearAll = () => {
    onSelectionChange([]);
  };

  const selectedCount = selectedValues.length;

  return (
    <div className="filter-item filter-dropdown hierarchical-location-dropdown">
      <label>
        {label}{' '}
        {isDisabled && <span style={{ color: '#999', fontSize: '11px' }}>(loading...)</span>}
      </label>
      <button
        className="dropdown-trigger"
        onClick={onToggle}
        disabled={isDisabled}
        style={{ opacity: isDisabled ? 0.6 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
      >
        <span className="dropdown-text">
          {isDisabled ? 'Loading data...' : selectedCount > 0 ? `${selectedCount} selected` : 'Select locations'}
        </span>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </button>

      {isOpen && (
        <div className="dropdown-menu hierarchical-dropdown-menu">
          <div className="dropdown-header">
            <span className="dropdown-title">{label}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="view-toggle-btn"
                onClick={() => setViewMode(viewMode === 'hierarchical' ? 'flat' : 'hierarchical')}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {viewMode === 'hierarchical' ? '📋 Flat' : '🗂️ Grouped'}
              </button>
              {selectedCount > 0 && (
                <button className="clear-selection-btn" onClick={clearAll}>
                  <FiX size={14} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Search Input */}
          <div className="dropdown-search">
            <FiSearch size={14} />
            <input
              type="text"
              placeholder={`Search locations...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="dropdown-search-input"
              autoFocus
            />
          </div>

          {/* Options */}
          <div className="dropdown-options hierarchical-options">
            {viewMode === 'hierarchical' && !searchTerm ? (
              // Hierarchical view
              Object.entries(hierarchy).sort((a, b) => a[0].localeCompare(b[0])).map(([region, countries]) => (
                <div key={region} className="region-group">
                  <div
                    className="region-header"
                    onClick={() => toggleRegion(region)}
                  >
                    <FiChevronRight
                      size={14}
                      style={{
                        transform: expandedRegions.has(region) ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={Object.values(countries).flat().every(loc => selectedValues.includes(loc))}
                      onChange={(e) => {
                        e.stopPropagation();
                        selectRegion(region);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="region-name">{region}</span>
                    <span className="count-badge">{Object.keys(countries).length}</span>
                  </div>

                  {expandedRegions.has(region) && (
                    <div className="country-list">
                      {Object.entries(countries).sort((a, b) => a[0].localeCompare(b[0])).map(([country, cities]) => (
                        <div key={country} className="country-group">
                          <div
                            className="country-header"
                            onClick={() => toggleCountry(country)}
                          >
                            <FiChevronRight
                              size={12}
                              style={{
                                transform: expandedCountries.has(country) ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }}
                            />
                            <input
                              type="checkbox"
                              checked={cities.every(loc => selectedValues.includes(loc))}
                              onChange={(e) => {
                                e.stopPropagation();
                                selectCountry(country);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="country-name">{country}</span>
                            <span className="count-badge">{cities.length}</span>
                          </div>

                          {expandedCountries.has(country) && (
                            <div className="city-list">
                              {cities.sort().map(city => (
                                <label key={city} className="dropdown-option city-option">
                                  <input
                                    type="checkbox"
                                    checked={selectedValues.includes(city)}
                                    onChange={() => toggleLocation(city)}
                                  />
                                  <span>{city}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              // Flat view (or search results)
              filteredOptions.length > 0 ? (
                filteredOptions.sort().map(option => (
                  <label key={option} className="dropdown-option">
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={() => toggleLocation(option)}
                    />
                    <span>{option}</span>
                  </label>
                ))
              ) : (
                <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                  {searchTerm ? `No results for "${searchTerm}"` : 'No locations available'}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HierarchicalLocationDropdown;
